import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { SessionService } from './session.service';
import { SubscriptionsService } from './subscriptions.service';
import { WorkspaceService } from './workspace.service';
import { SupabaseService } from './supabase.service';

@Injectable({
  providedIn: 'root'
})
export class UsageService {
  sessionService = inject(SessionService);
  subscriptionsService = inject(SubscriptionsService);
  workspaceService = inject(WorkspaceService);
  supabase = inject(SupabaseService);

  public creditCheckDialogVisible = signal(false);


  // Base signals derived directly from the source
  public readonly creditsUsed = computed(() => this.workspaceService.billingAccount()?.credits_used ?? 0);
  public readonly prepaidCredits = computed(() => this.workspaceService.billingAccount()?.prepaid_credit_balance ?? 0);
  
  // Subscriptions are a placeholder for now — billing is prepaid-only. Until
  // subscription plans ship, the subscription credit limit is always 0, so the
  // effective credit limit is driven entirely by the prepaid balance.
  public readonly creditLimitFromSubscription = computed(() => {
    const ws: any = this.workspaceService.activeWorkspace();
    return ws?.subscription_credit_limit ?? 0;
  });

  // The effective credit limit. Prepaid-only today (== prepaidCredits); once
  // subscriptions ship this also includes the monthly subscription allowance.
  public readonly totalCreditLimit = computed(() => 
    this.creditLimitFromSubscription() + this.prepaidCredits()
  );

  public readonly availableCredits = computed(() => 
    this.totalCreditLimit() - this.creditsUsed()
  );

  public readonly usedPercentage = computed(() => {
    const totalLimit = this.totalCreditLimit();
    // Guard against division by zero
    return totalLimit > 0 ? (100 * this.creditsUsed()) / totalLimit : 0;
  });

  public readonly prepaidThresholdPercentage = computed(() => {
    const totalLimit = this.totalCreditLimit();
    // Guard against division by zero
    return totalLimit > 0 ? (100 * this.prepaidCredits()) / totalLimit : 0;
  });

  public readonly displayBarPercentage = computed(() => 
    Math.min(this.usedPercentage(), 100)
  );

  public readonly renewalDate = computed(() => {
    const subscription = this.subscriptionsService.subscriptionSummary() ?? null;
    if (!subscription) return null;
    return new Date(subscription.current_period_end);
  });

  public readonly barFillColor = computed(() => {
    const percentage = this.usedPercentage();
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-orange-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-green-500';
  });

  constructor() {
    effect(() => {
      console.log('creditCheckDialogVisible', this.creditCheckDialogVisible());
    });
  }

  /**
   * Check if user has sufficient credits for an operation
   * @param requiredCredits - Number of credits required for the operation
   * @returns true if user has enough credits, false otherwise
   */
  public hasSufficientCredits(requiredCredits = 1): boolean {
    return this.availableCredits() >= requiredCredits;
  }

  /**
   * Show the credit check dialog when user runs out of credits
   */
  public showCreditCheckDialog(): void {
    console.log('showCreditCheckDialog');
    this.creditCheckDialogVisible.set(true);
  }

  /**
   * Hide the credit check dialog
   */
  public hideCreditCheckDialog(): void {
    console.log('hideCreditCheckDialog');
    this.creditCheckDialogVisible.set(false);
  }

  /**
   * Check credits and show dialog if insufficient
   * @param requiredCredits - Number of credits required for the operation
   * @returns true if user has enough credits, false otherwise
   * 
   * Should run this before any API call that uses credits.
   */
  public checkCreditsAndShowDialog(requiredCredits = 0): boolean {
    console.log('checkCreditsAndShowDialog', requiredCredits);
    if (!this.hasSufficientCredits(requiredCredits)) {
      // Only show dialog if it's not already visible to prevent infinite loops
      if (!this.creditCheckDialogVisible()) {
        this.showCreditCheckDialog();
      }
      return false;
    }
    return true;
  }
}
